import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import CustomerHeader from "../../components/CustomerHeader";
import { Button } from "../../components/ui/button";
import { Crown, Lock, Tag, Ticket, Check } from "lucide-react";
import { toast } from "sonner";

export default function FumaDeals() {
  const [data, setData] = useState(null);
  const [redeemed, setRedeemed] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/deals").then(({ data }) => setData(data)).catch(() => setData({ deals: [], is_plus: false }));
  useEffect(() => { load(); }, []);
  const [featured, setFeatured] = useState(new Set());
  useEffect(() => { api.get("/ads/active").then(({ data }) => setFeatured(new Set((data.featured_deals || []).map((f) => f.deal_id)))).catch(() => {}); }, []);

  const eur = (v) => v == null ? "" : "€" + Number(v).toFixed(2);

  const activatePlus = async () => {
    try {
      const { data } = await api.post("/customer/fuma-plus/subscribe", { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const redeem = async (d) => {
    setBusy(d.id);
    try {
      await api.post(`/deals/${d.id}/click`);
      const { data } = await api.post(`/deals/${d.id}/redeem`);
      setRedeemed((r) => ({ ...r, [d.id]: data.code }));
      toast.success(`Offerta riscattata! Codice: ${data.code}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const isPlus = data?.is_plus;
  const deals = [...(data?.deals || [])].sort((a, b) => (featured.has(b.id) ? 1 : 0) - (featured.has(a.id) ? 1 : 0));

  return (
    <div className="min-h-screen bg-[#0A0A0C] grain">
      <CustomerHeader />
      <main className="max-w-4xl mx-auto px-4 lg:px-8 py-8 relative z-10">
        <div className="flex items-center gap-3 mb-2">
          <Crown className="h-7 w-7 text-[#FFD700]" />
          <h1 className="font-display font-black text-3xl tracking-tighter">Fuma+ Deals</h1>
        </div>
        <p className="text-zinc-400 mb-6">Offerte esclusive riservate ai membri Fuma+ da attività, negozi e professionisti del territorio.</p>

        {!isPlus && (
          <div data-testid="deals-plus-upsell" className="rounded-xl p-5 mb-6 border border-[#FFD700]/40 bg-gradient-to-br from-[#FFD700]/12 to-[#131316] flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3"><Lock className="h-6 w-6 text-[#FFD700]" /><div><div className="font-semibold">Sblocca tutte le offerte</div><div className="text-xs text-zinc-400">Attiva Fuma+ per riscattare i prezzi riservati ai membri.</div></div></div>
            <Button data-testid="deals-activate-plus" onClick={activatePlus} className="bg-[#00E676] text-black rounded-full font-semibold">Attiva Fuma+</Button>
          </div>
        )}

        {data && deals.length === 0 && (
          <div data-testid="deals-empty" className="text-zinc-500 py-16 text-center">Nessuna offerta Fuma+ disponibile al momento. Torna presto!</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {deals.map((d) => {
            const soldOut = d.effective_status === "sold_out";
            const code = redeemed[d.id];
            return (
              <div key={d.id} data-testid={`deal-card-${d.id}`} className="rounded-xl overflow-hidden bg-[#1C1C1F] border border-white/10 flex flex-col">
                {d.image ? <img src={d.image} alt={d.title} className="h-36 w-full object-cover" /> : <div className="h-36 w-full bg-gradient-to-br from-[#FFD700]/15 to-[#0A0A0C] flex items-center justify-center"><Tag className="h-8 w-8 text-[#FFD700]/60" /></div>}
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display font-bold text-lg leading-tight">{d.title}</div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-[10px] bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40 rounded-full px-2 py-0.5 font-bold">FUMA+</span>
                      {featured.has(d.id) && <span data-testid={`deal-featured-${d.id}`} className="text-[10px] bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40 rounded-full px-2 py-0.5 font-bold">✦ In evidenza</span>}
                    </div>
                  </div>
                  {d.business_name && <div className="text-xs text-zinc-500 mt-0.5">{d.business_name}</div>}
                  {d.description && <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{d.description}</p>}
                  <div className="flex items-end gap-2 mt-3">
                    {d.original_price != null && d.plus_price != null && d.plus_price < d.original_price && (
                      <span className="text-zinc-500 line-through text-sm">{eur(d.original_price)}</span>
                    )}
                    {d.plus_price != null ? <span className="text-2xl font-black text-[#FFD700]">{eur(d.plus_price)}</span>
                      : d.discount_type === "percent" ? <span className="text-2xl font-black text-[#FFD700]">-{d.discount_value}%</span>
                      : <span className="text-2xl font-black text-[#FFD700]">-{eur(d.discount_value)}</span>}
                  </div>
                  <div className="mt-auto pt-4">
                    {code ? (
                      <div data-testid={`deal-code-${d.id}`} className="rounded-lg bg-[#0A0A0C] border border-[#00E676]/40 p-3 text-center">
                        <div className="text-xs text-zinc-500">Mostra questo codice all'attività</div>
                        <div className="font-mono font-bold text-[#00E676] text-lg tracking-wider">{code}</div>
                      </div>
                    ) : !isPlus ? (
                      <Button data-testid={`deal-locked-${d.id}`} onClick={activatePlus} variant="outline" className="w-full rounded-full border-[#FFD700]/40 text-[#FFD700]"><Lock className="h-4 w-4 mr-1" /> Riservata Fuma+</Button>
                    ) : soldOut ? (
                      <Button disabled className="w-full rounded-full opacity-50">Esaurita</Button>
                    ) : (
                      <Button data-testid={`deal-redeem-${d.id}`} disabled={busy === d.id} onClick={() => redeem(d)} className="w-full bg-[#FFD700] text-black rounded-full font-semibold hover:bg-[#e6c200]"><Ticket className="h-4 w-4 mr-1" /> {busy === d.id ? "Riscatto…" : "Riscatta offerta"}</Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
